import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGroup } from '@fortawesome/pro-light-svg-icons/faUserGroup';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansEmptyProps {
  accountId: number;
  /** False on Free: both buttons open the plan prompt (design c4 note). */
  canUsePresave: boolean;
}

/**
 * "No subscribers yet": the two ways a fan subscribes and the two buttons (design c4). "Add the
 * Get every release block" opens the SmartLinks list; "Create a pre-save" opens the pre-save wizard.
 */
const FansEmptyComponent: React.FC<FansEmptyProps> = ({ accountId, canUsePresave }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgradePrompt = useUpgradePrompt();

  const addBlock = useCallback(() => {
    if (!canUsePresave) {
      openUpgradePrompt(SMLUpgradeFeature.presave);
      return;
    }
    router.push(SMLRoutes.smartlinks);
  }, [canUsePresave, openUpgradePrompt, router]);

  const createPresave = useCallback(() => {
    if (!canUsePresave) {
      openUpgradePrompt(SMLUpgradeFeature.presave);
      return;
    }
    router.push(SMLRoutes.presaveLink, { accountId });
  }, [accountId, canUsePresave, openUpgradePrompt, router]);

  const ways = useMemo(
    () =>
      [
        { title: messages.emptyWayPresaveTitle, text: messages.emptyWayPresaveText },
        { title: messages.emptyWayBlockTitle, text: messages.emptyWayBlockText },
      ].map((way, index) => (
        <div key={way.title.id} className={styles.way}>
          <span className={styles.wayNumber}>{intl.formatMessage(messages.emptyWay, { number: index + 1 })}</span>
          <b>{intl.formatMessage(way.title)}</b>
          <p>{intl.formatMessage(way.text)}</p>
        </div>
      )),
    [intl]
  );

  return (
    <div className={styles.empty} data-testid="fans-empty">
      <span className={styles.emptyIcon}>
        <FontAwesomeIcon icon={faUserGroup} />
      </span>
      <h3>{intl.formatMessage(messages.emptyTitle)}</h3>
      <p className={styles.emptyText}>{intl.formatMessage(messages.emptyText)}</p>
      <div className={styles.ways}>{ways}</div>
      <div className={styles.emptyActions}>
        <SMLButton color="primary" omitCap leftIcon={faPlus} onClick={addBlock}>
          {intl.formatMessage(messages.emptyAddBlock)}
        </SMLButton>
        <SMLButton outline omitCap onClick={createPresave}>
          {intl.formatMessage(messages.emptyCreatePresave)}
        </SMLButton>
      </div>
    </div>
  );
};

export const FansEmpty = TypedMemo(FansEmptyComponent);
